// In-memory print dedup guard.
// Print history does not need to survive app restarts — a Map is sufficient.

const MAX_ENTRIES = 20000;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// Map<orderId, timestamp>
const printedOrders = new Map();

/**
 * Checks if the order has already been printed in this session.
 * @param {string} orderId
 * @returns {Promise<boolean>}
 */
export const hasPrintedLocal = async (orderId) => {
  if (!orderId) return false;
  return printedOrders.has(orderId);
};

/**
 * Marks an order as printed.
 * @param {string} orderId
 */
export const markPrintedLocal = async (orderId) => {
  if (!orderId) return;
  printedOrders.set(orderId, Date.now());

  // Optional: Auto-cleanup (1% chance to run)
  if (Math.random() < 0.01) {
    cleanOldRecords();
  }
};

/**
 * BUG-1971: atomic check-and-claim for concurrent callers.
 * `await hasPrintedLocal(id)` followed by `await markPrintedLocal(id)` as two
 * separate awaited calls is NOT atomic -- two callers started back-to-back
 * (e.g. via Promise.all, or two event handlers firing within the same tick)
 * both suspend at their own `await` before either reaches the `set`, so both
 * see "not printed" and both proceed. This function has no internal await, so
 * the check and the set happen in one uninterruptible synchronous turn:
 * whichever caller's JS execution reaches it first wins outright.
 * @param {string} orderId
 * @returns {boolean} true if this call just claimed the order, false if another caller already holds it
 */
export const claimPrintOnce = (orderId) => {
  if (!orderId) return true;
  if (printedOrders.has(orderId)) return false;
  printedOrders.set(orderId, Date.now());

  if (Math.random() < 0.01) {
    cleanOldRecords();
  }
  return true;
};

const cleanOldRecords = () => {
  if (printedOrders.size > MAX_ENTRIES) {
    const cutoff = Date.now() - THIRTY_DAYS_MS;
    for (const [id, ts] of printedOrders) {
      if (ts < cutoff) {
        printedOrders.delete(id);
      }
    }
  }
};
